/**
 * L2 实时订阅（端侧）—— socket.io 连接 + 账本订阅 + **缓存失效**
 *
 * 依据：规格 **§16**（并发冲突策略）· L 域 PRD **§5.2 口径 6–7** · **§7.2**（`AC-L2.7`–`L2.9`）·
 * 架构 **§7.2**（客户端呈现策略）· **§10.2**（TanStack Query 缓存失效模型配合 WS 增量刷新）。
 *
 * ## 🔴 三条设计决定
 *
 * 1. **失效重取，不拼行**（架构 §10.2）—— 事件载荷**不带业务字段**（契约层明文），
 *    端侧收到后只做 `queryClient.invalidateQueries()` ⇒ 行装配永远只有
 *    `GET /api/...` 那一份实现，WS 增量不可能与它不一致。
 * 2. **每个事件只失效"跟它有关"的 key**（架构 §7.2 的按域失效）——
 *    key 前缀清单在 {@link INVALIDATIONS}，是**事件 → 查询域**映射的唯一实现。
 * 3. **连接是模块级单例，断线交给 socket.io 内建重连**（`reconnection: true`，
 *    指数退避）—— 端侧不自写重试循环（那会造出第二个重连实现，与订阅状态互相踩）。
 *
 * ## ⚠️ 提示条（本批 `66` 已接上一半 —— 见 `features/realtime-toast.ts` 文件头）
 *
 * - ✅ **顶部轻提示条**（`AC-L2.8` 的"不在流水页"半边）已接：本文件在**同一个 socket 回调**里
 *   先 `shouldToast()` 过闸、再 `pushRealtimeEvent()` 喂给全局 store，`Shell` 渲染 `RealtimeToast`。
 * - ❌ **「在流水页 ⇒ 静默插入 + 高亮 3 秒」未做** —— 它是**时序/几何**行为，本项目的端侧测试基座
 *   （`react-dom/server`，不跑 effect、无 layout engine）**证不了**（铁律 43）⇒ 显式留空并登记。
 * - 🔴 **为什么过闸 + 喂 store 必须在这个回调里**（而不是各自再来一遍）：三样东西只有这里同时拿得到
 *   —— `session`（含 `userId`）、事件对、以及"这条已经被失效处理过"的事实。
 *   在别处再筛一遍 = **第二份归属判定**，与 `features/realtime-toast.ts` 迟早漂移（铁律 24）。
 */

import { useEffect } from 'react';
import { io } from 'socket.io-client';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { RealtimeEvent, type RealtimeEventMap } from '@jiaying/shared';
import { useApp } from '../app/store.js';
import { shouldToast, type ToastEvent } from '../features/realtime-toast.js';
import type { Session } from './http.js';

/** 事件 → 要失效的 query key 前缀（🔴 映射只此一份；新 query key 接入实时时在这里登记） */
const INVALIDATIONS: { [K in keyof RealtimeEventMap]: readonly (readonly string[])[] } = {
  // ① 流水变化 ⇒ 列表重取，且账户余额 / 净资产 / 预算 / 贡献都随腿变
  [RealtimeEvent.Txn]: [
    ['transactions'],
    ['accounts'],
    ['networth'],
    ['budgets'],
    ['contributions'],
    ['upcoming'],
  ],
  // ② 账户变化 ⇒ 账户列表 / 净资产（余额、隐藏态都可能变）
  [RealtimeEvent.Account]: [['accounts'], ['networth']],
  // ③ 预算档位（H 域接线后生效）⇒ 预算卡
  [RealtimeEvent.Budget]: [['budgets']],
  // ④ 成员变动 ⇒ 成员列表 / 账本信息
  [RealtimeEvent.Member]: [['members'], ['ledger'], ['accounts']],
};

/** socket.io 的 `Socket` 实例类型（用 `ReturnType` 推导，避免直接依赖其类型面） */
type RealtimeSocket = ReturnType<typeof io>;
let socket: RealtimeSocket | null = null;
/** 当前已订阅的账本 —— 与 `session.ledgerId` 比对，变了才发 unsubscribe/subscribe */
let subscribedLedgerId: string | null = null;

/**
 * 🆕 **提示条的投递口**（批次 `66`）—— 由 `useRealtime()` 挂上当前的 store dispatch。
 *
 * ## 🔴 为什么需要这个"模块级可变函数"（而不是把 dispatch 传进 `ensureSocket`）
 *
 * `ensureSocket` 建连**只做一次**（token 不变就复用），而 `dispatch` 是 **React 的**、会随
 * `useMemo` 重建引用。若把 dispatch 闭包进 `socket.on` 的回调里，**首连那次**的 dispatch
 * 会被永久记住 —— 一旦 store 的 value 重建（`AppProvider` 重渲染），提示条就会投给**旧的**
 * dispatch（React 的 dispatch 本身稳定，但这条路径制造了一个"看起来能用、实际用旧的"陷阱）。
 * ⇒ 改为**每次 `useRealtime()` 运行都刷新这个引用**，回调永远用**最新的**那个。
 *
 * ⚠️ 与铁律 47 反面（"单例服务禁存请求级实例字段"）的**区别**：这里存的是**指向全局 store
 * 的稳定投递函数**（生命周期 = 整个应用，不是某个请求），且每次 render 都覆盖为最新 ⇒
 * 不构成那个坑（那坑是"把请求级数据存进单例"）。仍然**诚实登记**：它是一个模块级可变量，
 * 是"订阅在模块级单例、而 store 在 React 里"这一结构落差的直接代价。
 */
let deliverToast: (event: ToastEvent) => void = () => {};

/** 🆕 过闸所需的上下文（同样每次 `useRealtime()` 运行刷新 —— 换账本/重新登录后必须跟着变） */
let toastContext: { ledgerId: string; userId: string } = { ledgerId: '', userId: '' };

/** 给 queryClient 打失效（单一入口：日志 + 白名单都在这里） */
function invalidate(qc: QueryClient, event: keyof RealtimeEventMap, payload: RealtimeEventMap[keyof RealtimeEventMap]): void {
  const prefixes = INVALIDATIONS[event];
  if (prefixes === undefined) return;
  for (const prefix of prefixes) {
    void qc.invalidateQueries({ queryKey: [...prefix] });
  }
  void payload; // 载荷当前只用于排查（契约明确不携带业务字段）
}

/** 建连或复用；token 变了（重新登录）才重建 */
function ensureSocket(session: Session, qc: QueryClient): RealtimeSocket {
  const token = session.token;
  if (socket !== null && socket.auth !== undefined) {
    // 已有连接：token 相同就复用，不同就重连（重新登录 = 新身份）
    const auth = socket.auth as { token?: string };
    if (auth.token === token) return socket;
    socket.disconnect();
    socket = null;
  }
  const s = io('/', {
    // 🔴 握手带 token（网关 handleConnection 验证并缓存身份）
    auth: { token },
    reconnection: true,
    // 连不上（后端没起 / 网络差）⇒ 安静重试，绝不弹窗（推送是增强，不是依赖）
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 15_000,
  });
  // 4 类事件各挂一个监听 —— 事件名取自共享层闭集（写错即编译错误）
  for (const event of Object.values(RealtimeEvent)) {
    s.on(event, (payload: RealtimeEventMap[typeof event]) => {
      // 🔴 账本过滤：切账本后晚到的旧账本事件直接丢弃（契约层 `ledgerId` 字段的存在理由）
      if (payload.ledgerId !== session.ledgerId) return;
      invalidate(qc, event, payload);

      // 🆕 提示条（批次 `66`）—— 🔴 **闸门在 `features/realtime-toast.ts`（唯一一份）**，
      //    这里只做"过闸 ⇒ 投递"。**不重写判定**（写了就是第二份归属规则，迟早漂移）。
      //    ⚠️ 它读的是模块级 `toastContext`（每次 `useRealtime` 刷新）而**不是**闭包里的
      //    `session` —— 那条闭包是**首连时**的会话，换账本后会指向旧账本（见 `deliverToast` 说明）。
      const ev: ToastEvent = { event, payload };
      if (shouldToast(ev, toastContext)) deliverToast(ev);
    });
  }
  socket = s;
  subscribedLedgerId = null; // 新连接需要重新订阅
  return s;
}

/** 按 `session.ledgerId` 对齐订阅状态（进房 / 换房 / 退房） */
function alignSubscription(session: Session, s: RealtimeSocket): void {
  if (subscribedLedgerId === session.ledgerId) return;
  if (subscribedLedgerId !== null) {
    s.emit('unsubscribe', { ledgerId: subscribedLedgerId });
  }
  s.emit('subscribe', { ledgerId: session.ledgerId });
  subscribedLedgerId = session.ledgerId;
}

/**
 * 挂在 `Root`（`QueryClientProvider` 之内）—— 会话变化时维护连接与订阅。
 *
 * 🔴 未登录 ⇒ 断开并清状态（登录页不需要推送，也不该带着旧 token 连着）。
 *
 * 🆕 批次 `66`：同时把**提示条的投递口与过闸上下文**刷新成当前值（见 `deliverToast` 说明）。
 * 🔴 刷新动作**在函数体里同步做**（不在 effect 里）—— socket 回调**不等 effect**，
 * 若挪进 effect，切换账本后到 effect 跑之前的那批包会**用旧账本过闸**：
 * ① 旧账本事件（本该丢）被投递；② 新账本事件（本该投）被账本闸挡掉（提示条静默失效）。
 */
export function useRealtime(): void {
  const { session, pushRealtimeEvent } = useApp();
  const qc = useQueryClient();

  // 🔴 每次 render 同步刷新：dispatch 与 session 都取本次 render 的最新值。
  deliverToast = pushRealtimeEvent;
  toastContext = {
    ledgerId: session?.ledgerId ?? '',
    userId: session?.userId ?? '',
  };

  useEffect(() => {
    if (session === null) {
      socket?.disconnect();
      socket = null;
      subscribedLedgerId = null;
      // 🔴 登出后上下文清空 —— 否则未登录状态下若还有在途事件，会拿上一个身份的 userId 过闸
      toastContext = { ledgerId: '', userId: '' };
      return;
    }
    const s = ensureSocket(session, qc);
    // 连接就绪后才订阅（首次连接 / 断线重连都会触发 connect）
    const onConnect = (): void => {
      subscribedLedgerId = null;
      alignSubscription(session, s);
    };
    if (s.connected) alignSubscription(session, s);
    s.on('connect', onConnect);
    return () => {
      s.off('connect', onConnect);
    };
  }, [session, qc]);
}
